import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Form, Button, Badge, Alert, Spinner, ProgressBar } from "react-bootstrap";
import Footer from "../layouts/Footer";
import * as XLSX from 'xlsx';
import Select from "react-select";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ImportContacts() {
  const [importStep, setImportStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('idle');
  const [importStats, setImportStats] = useState({});
  const [sending, setSending] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const expectedColumns = [
    { key: 'nom', label: 'Nom', required: true, example: 'MOUBASSANGO', description: 'Nom de famille du contact' },
    { key: 'prenom', label: 'Prénom', required: true, example: 'Marie', description: 'Prénom du contact' },
    { key: 'numero', label: 'Téléphone', required: true, example: '24176542759', description: 'Numéro au format Gabon (241...) ou international' },
    { key: 'entreprise', label: 'Entreprise', required: false, example: 'ABC Corporation', description: 'Nom de l\'entreprise' },
    { key: 'fonction', label: 'Fonction', required: false, example: 'Commercial', description: 'Poste du contact' }
  ];

  // ============================================
  // TÉLÉCHARGEMENT DES MODÈLES
  // ============================================
  const downloadTemplateCSV = () => {
    const headers = ['nom', 'prenom', 'numero', 'entreprise', 'fonction'];
    const examples = ['MOUBASSANGO', 'Marie', '24176542759', 'ABC Corporation', 'Commercial'];
    const csvContent = [
      headers.join(','),
      examples.join(','),
      'MARTIN,Pierre,24177513526,XYZ Inc,Manager',
      'NDONG,Jean,24177012345,Minitrade,Assistant'
    ].join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "modele-import-contacts.csv";
    link.click();
  };

  const downloadTemplateExcel = () => {
    const data = [
      ['nom', 'prenom', 'numero', 'entreprise', 'fonction'],
      ['MOUBASSANGO', 'Marie', '24176542759', 'ABC Corporation', 'Commercial'],
      ['MBINA', 'Pierre', '24166640807', 'XYZ Inc', 'Manager'],
      ['MAROUNDOU', 'Polette', '24177851382', 'Minitrade', 'Assistant'],
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
    XLSX.writeFile(workbook, "modele-import-contacts.xlsx");
  };

  // ============================================
  // GESTION DU FICHIER
  // ============================================
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = (file) => {
    const allowedExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      alert('Veuillez sélectionner un fichier CSV ou Excel valide (.csv, .xls, .xlsx)');
      return;
    }

    setSelectedFile(file);
    previewFile(file);
  };

  // ✅ Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  // ============================================
  // CHARGEMENT DES GROUPES
  // ============================================
  const loadContactGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await apiClient.get('/contact-groups');
      const data = response.data;

      if (data.code === 200 && data.data) {
        setContactGroups(data.data);
      } else {
        setContactGroups(data);
      }
    } catch (error) {
      console.error('Erreur groupes:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    loadContactGroups();
  }, []);

  // ============================================
  // PRÉVISUALISATION
  // ============================================
  const previewFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        let previewData = [];

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          const content = e.target.result;
          const lines = content.split('\n').slice(0, 6);
          previewData = lines.map(line => {
            const regex = /(?:,|\n|^)("(?:(?:"")*[^"]*)*"|[^",\n]*|(?:\n|$))/g;
            const matches = line.match(regex);
            return matches ? matches.map(cell =>
              cell.replace(/^,/, '').replace(/^"|"$/g, '').replace(/""/g, '"').trim()
            ) : line.split(',').map(cell => cell.trim());
          });
        } else {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 6);
        }

        setFilePreview(previewData);

        if (previewData.length > 0) {
          validateFileData(previewData);
        }
      } catch (error) {
        console.error('Erreur lecture:', error);
        alert('Erreur lors de la lecture du fichier.');
      }
    };

    if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file, 'UTF-8');
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // ============================================
  // VALIDATION
  // ============================================
  const validateFileData = (previewData) => {
    const results = { valid: 0, invalid: 0, duplicates: 0, errors: [], columnMatch: true };

    if (previewData.length > 0) {
      const headers = previewData[0];
      const expectedHeaders = ['nom', 'prenom', 'numero', 'entreprise', 'fonction'];
      const hasExpectedHeaders = expectedHeaders.every(header =>
        headers.some(h => h && h.toLowerCase().trim() === header)
      );

      if (!hasExpectedHeaders) {
        results.columnMatch = false;
        results.errors.push("Les colonnes ne correspondent pas au format attendu.");
      }
    }

    if (previewData.length > 1 && results.columnMatch) {
      previewData.slice(1).forEach((row, index) => {
        const nomStr = row[0] ? String(row[0]).trim() : '';
        const prenomStr = row[1] ? String(row[1]).trim() : '';
        const numeroStr = row[2] ? String(row[2]).trim() : '';

        if (!nomStr || !prenomStr || !numeroStr) {
          results.invalid++;
          results.errors.push(`Ligne ${index + 2}: Champs obligatoires manquants`);
        } else if (!isValidPhoneNumber(numeroStr)) {
          results.invalid++;
          results.errors.push(`Ligne ${index + 2}: Numéro invalide "${numeroStr}"`);
        } else {
          results.valid++;
        }
      });
    }

    setValidationResults(results);
    setImportStep(2);
  };

  const isValidPhoneNumber = (numero) => {
    const cleaned = numero.replace(/\s+/g, '');
    return /^241[0-9]{8}$/.test(cleaned) || /^\+?[0-9]{10,15}$/.test(cleaned);
  };

  // ============================================
  // UPLOAD
  // ============================================
  const uploadFileViaProxy = async () => {
    if (!selectedFile) {
      alert('Aucun fichier sélectionné');
      return;
    }

    setSending(true);
    setImportStatus('importing');
    setImportProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const groupesData = Array.isArray(selectedGroups) ? selectedGroups : [];
      if (groupesData.length > 0) {
        formData.append('groupes', JSON.stringify(groupesData));
      }

      setImportProgress(30);
      const response = await apiClient.post('/import-contacts', formData);
      setImportProgress(70);

      const result = response.data;
      setImportProgress(100);
      setUploadResults(result);
      setImportStatus('success');
      setImportStats({
        total: filePreview.length - 1,
        imported: validationResults.valid,
        failed: validationResults.invalid,
        jobId: result.job_id || result.id || null
      });
    } catch (error) {
      console.error('❌ Erreur import:', error);
      setImportStatus('error');
      setUploadResults({ error: error.message, success: false });
    } finally {
      setSending(false);
      setImportStep(3);
    }
  };

  const resetImport = () => {
    setImportStep(1);
    setSelectedFile(null);
    setFilePreview([]);
    setValidationResults({});
    setImportProgress(0);
    setImportStatus('idle');
    setImportStats({});
    setUploadResults(null);
    setSelectedGroups([]);
  };

  return (
    <div className="ic-page main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="ic-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="ic-header-icon">
              <i className="bi bi-file-earmark-arrow-up"></i>
            </div>
            <div>
              <h3 className="ic-header-title mb-1">Importer des contacts</h3>
              <p className="ic-header-subtitle mb-0">
                Importez vos contacts depuis un fichier CSV ou Excel
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Link to="/apps/ListeContacts" className="ic-btn-secondary">
              <i className="bi bi-arrow-left"></i>
              <span>Retour</span>
            </Link>
            <button className="ic-btn-secondary" onClick={downloadTemplateCSV}>
              <i className="bi bi-file-text"></i>
              <span>Modèle CSV</span>
            </button>
            <button className="ic-btn-primary" onClick={downloadTemplateExcel}>
              <i className="bi bi-file-excel"></i>
              <span>Modèle Excel</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========== STEPPER ========== */}
      <div className="ic-stepper mb-4">
        <div className="ic-stepper-wrapper">
          {[
            { n: 1, label: 'Import fichier', icon: 'bi-cloud-upload' },
            { n: 2, label: 'Validation', icon: 'bi-check2-square' },
            { n: 3, label: 'Résultats', icon: 'bi-trophy' }
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div className={`ic-step ${importStep >= step.n ? 'active' : ''} ${importStep === step.n ? 'current' : ''}`}>
                <div className="ic-step-circle">
                  {importStep > step.n ? <i className="bi bi-check-lg"></i> : <i className={`bi ${step.icon}`}></i>}
                </div>
                <div className="ic-step-label">{step.label}</div>
              </div>
              {i < 2 && <div className={`ic-step-line ${importStep > step.n ? 'active' : ''}`}></div>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ========== ÉTAPE 1 : UPLOAD ========== */}
      {importStep === 1 && (
        <Row>
          <Col lg={8} className="mx-auto">
            <div className="ic-upload-card">
              <div className="ic-upload-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="ic-upload-icon">
                    <i className="bi bi-cloud-upload"></i>
                  </div>
                  <div>
                    <h6 className="ic-upload-title mb-0">Importer un fichier</h6>
                    <small className="ic-upload-subtitle">CSV, Excel (.xlsx, .xls)</small>
                  </div>
                </div>
              </div>

              <div className="ic-upload-body">
                <div
                  className={`ic-dropzone ${isDragging ? 'dragging' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="ic-dropzone-icon">
                    <i className="bi bi-cloud-arrow-up"></i>
                  </div>
                  <h5 className="ic-dropzone-title">Glissez-déposez votre fichier ici</h5>
                  <p className="ic-dropzone-text">
                    ou cliquez pour sélectionner un fichier<br />
                    <strong>Formats :</strong> CSV, Excel (.xlsx, .xls)
                  </p>
                  <button className="ic-btn-primary" type="button">
                    <i className="bi bi-folder-open"></i>
                    Sélectionner un fichier
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="ic-format-section mt-4">
                  <h6 className="ic-format-title">
                    <i className="bi bi-table"></i>
                    Format attendu
                  </h6>
                  <div className="ic-format-table">
                    <div className="ic-format-row ic-format-header">
                      <div className="ic-format-cell">Colonne</div>
                      <div className="ic-format-cell">Requis</div>
                      <div className="ic-format-cell">Exemple</div>
                      <div className="ic-format-cell">Description</div>
                    </div>
                    {expectedColumns.map(column => (
                      <div key={column.key} className="ic-format-row">
                        <div className="ic-format-cell">
                          <strong>{column.label}</strong>
                        </div>
                        <div className="ic-format-cell">
                          {column.required ? (
                            <span className="ic-badge ic-badge-danger">Obligatoire</span>
                          ) : (
                            <span className="ic-badge ic-badge-gray">Optionnel</span>
                          )}
                        </div>
                        <div className="ic-format-cell">
                          <code className="ic-code">{column.example}</code>
                        </div>
                        <div className="ic-format-cell ic-format-desc">
                          {column.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ========== ÉTAPE 2 : VALIDATION ========== */}
      {importStep === 2 && (
        <Row>
          <Col lg={12}>
            <div className="ic-validation-card">
              <div className="ic-validation-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="ic-validation-icon">
                    <i className="bi bi-check2-square"></i>
                  </div>
                  <div>
                    <h6 className="ic-validation-title mb-0">Validation des données</h6>
                    <small className="ic-validation-subtitle">
                      Fichier : <strong>{selectedFile?.name}</strong>
                    </small>
                  </div>
                </div>
              </div>

              <div className="ic-validation-body">
                {importStatus === 'importing' && (
                  <div className="ic-sending-state">
                    <div className="ic-sending-icon">
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                    <h4 className="ic-sending-title">Import en cours...</h4>
                    <div className="ic-progress ic-progress-large">
                      <div className="ic-progress-bar ic-progress-bar-animated" style={{ width: `${importProgress}%` }}></div>
                    </div>
                    <p className="ic-sending-status">{importProgress}%</p>
                    <p className="ic-sending-hint">
                      <i className="bi bi-hourglass-split"></i>
                      Traitement des contacts...
                    </p>
                  </div>
                )}

                {!validationResults.columnMatch && (
                  <div className="ic-alert ic-alert-warning mb-4">
                    <i className="bi bi-exclamation-triangle"></i>
                    <div>
                      <strong>Format de fichier incorrect</strong>
                      <div>Vérifiez que votre fichier contient les colonnes : <strong>nom, prenom, numero, entreprise, fonction</strong></div>
                    </div>
                  </div>
                )}

                {/* STATS */}
                <div className="ic-stats-grid mb-4">
                  <div className="ic-stat-card ic-stat-green">
                    <div className="ic-stat-icon-wrapper ic-stat-icon-green">
                      <i className="bi bi-check-circle"></i>
                    </div>
                    <div className="ic-stat-content">
                      <div className="ic-stat-label">Valides</div>
                      <div className="ic-stat-value">{validationResults.valid || 0}</div>
                    </div>
                  </div>
                  <div className="ic-stat-card ic-stat-amber">
                    <div className="ic-stat-icon-wrapper ic-stat-icon-amber">
                      <i className="bi bi-exclamation-triangle"></i>
                    </div>
                    <div className="ic-stat-content">
                      <div className="ic-stat-label">Doublons</div>
                      <div className="ic-stat-value">{validationResults.duplicates || 0}</div>
                    </div>
                  </div>
                  <div className="ic-stat-card ic-stat-red">
                    <div className="ic-stat-icon-wrapper ic-stat-icon-red">
                      <i className="bi bi-x-circle"></i>
                    </div>
                    <div className="ic-stat-content">
                      <div className="ic-stat-label">Invalides</div>
                      <div className="ic-stat-value">{validationResults.invalid || 0}</div>
                    </div>
                  </div>
                  <div className="ic-stat-card ic-stat-blue">
                    <div className="ic-stat-icon-wrapper ic-stat-icon-blue">
                      <i className="bi bi-list-ol"></i>
                    </div>
                    <div className="ic-stat-content">
                      <div className="ic-stat-label">Total lignes</div>
                      <div className="ic-stat-value">{filePreview.length - 1}</div>
                    </div>
                  </div>
                </div>

                {/* APERÇU */}
                <div className="ic-section">
                  <h6 className="ic-section-title">
                    <i className="bi bi-eye"></i>
                    Aperçu du fichier
                  </h6>
                  <div className="ic-table-wrapper">
                    <table className="ic-table">
                      <thead>
                        <tr>
                          {filePreview[0]?.map((header, index) => (
                            <th key={index}>{header || `Colonne ${index + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filePreview.slice(1, 6).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {cellIndex < 3 ? (
                                  <span className={!cell ? 'ic-cell-empty' : ''}>
                                    {cell || <em>vide</em>}
                                  </span>
                                ) : (cell || '—')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ERREURS */}
                {validationResults.errors && validationResults.errors.length > 0 && (
                  <div className="ic-section">
                    <h6 className="ic-section-title ic-section-title-danger">
                      <i className="bi bi-exclamation-triangle"></i>
                      Erreurs de validation ({validationResults.errors.length})
                    </h6>
                    <div className="ic-errors-list">
                      {validationResults.errors.slice(0, 10).map((error, index) => (
                        <div key={index} className="ic-error-item">
                          <i className="bi bi-x-circle"></i>
                          <span>{error}</span>
                        </div>
                      ))}
                      {validationResults.errors.length > 10 && (
                        <div className="ic-error-more">
                          ... et {validationResults.errors.length - 10} autres erreurs
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* GROUPES */}
                <div className="ic-section">
                  <h6 className="ic-section-title">
                    <i className="bi bi-tags"></i>
                    Attribution aux groupes <span className="ic-optional">(optionnel)</span>
                  </h6>
                  <div className="ic-groups-card">
                    {loadingGroups ? (
                      <div className="ic-loading-inline">
                        <Spinner animation="border" size="sm" />
                        <span>Chargement des groupes...</span>
                      </div>
                    ) : contactGroups.length > 0 ? (
                      <>
                        <Select
                          isMulti
                          value={contactGroups
                            .filter(group => selectedGroups.includes(group.id))
                            .map(group => ({ value: group.id, label: group.name }))}
                          options={contactGroups.map(group => ({ value: group.id, label: group.name }))}
                          onChange={(selected) => {
                            const ids = selected ? selected.map(o => o.value) : [];
                            setSelectedGroups(ids);
                          }}
                          placeholder="Sélectionnez un ou plusieurs groupes..."
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: '10px',
                              borderColor: '#e2e8f0',
                              minHeight: '44px',
                              boxShadow: 'none',
                              '&:hover': { borderColor: '#3b82f6' }
                            }),
                            multiValue: (base) => ({ ...base, backgroundColor: '#eff6ff' }),
                            multiValueLabel: (base) => ({ ...base, color: '#3b82f6', fontWeight: 600 }),
                            multiValueRemove: (base) => ({
                              ...base,
                              color: '#3b82f6',
                              ':hover': { backgroundColor: '#3b82f6', color: 'white' }
                            })
                          }}
                        />
                        {selectedGroups.length > 0 && (
                          <div className="ic-groups-info">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>
                              <strong>{selectedGroups.length}</strong> groupe{selectedGroups.length > 1 ? 's' : ''} sélectionné{selectedGroups.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="ic-alert ic-alert-info">
                        <i className="bi bi-info-circle"></i>
                        <div>Aucun groupe disponible. Créez-en dans la section "Groupes".</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ic-actions-row">
                  <button className="ic-btn-secondary" onClick={() => setImportStep(1)}>
                    <i className="bi bi-arrow-left"></i>
                    Retour
                  </button>
                  <button
                    className="ic-btn-primary"
                    onClick={uploadFileViaProxy}
                    disabled={!validationResults.columnMatch || validationResults.valid === 0 || sending}
                  >
                    {sending ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Import en cours...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-play-circle"></i>
                        Démarrer l'import
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ========== ÉTAPE 3 : RÉSULTAT ========== */}
      {importStep === 3 && (
        <Row>
          <Col lg={8} className="mx-auto">
            <div className={`ic-result-card ${importStatus === 'success' ? 'ic-result-success' : 'ic-result-error'}`}>
              <div className="ic-result-header">
                <div className={`ic-result-icon ${importStatus === 'success' ? 'ic-result-icon-success' : 'ic-result-icon-error'}`}>
                  <i className={importStatus === 'success' ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'}></i>
                </div>
                <div>
                  <h4 className="ic-result-title">
                    {importStatus === 'success' ? 'Import terminé avec succès' : 'Erreur lors de l\'import'}
                  </h4>
                  <p className="ic-result-subtitle">
                    {importStatus === 'success' ? 'Vos contacts ont été importés' : 'Veuillez réessayer'}
                  </p>
                </div>
              </div>

              <div className="ic-result-body">
                {importStatus === 'success' ? (
                  <>
                    {importStats.jobId && (
                      <div className="ic-result-job">
                        <i className="bi bi-hash"></i>
                        ID Job : <strong>{importStats.jobId}</strong>
                      </div>
                    )}

                    <div className="ic-result-stats">
                      <div className="ic-result-stat ic-result-stat-success">
                        <div className="ic-result-stat-value">{importStats.imported || 0}</div>
                        <div className="ic-result-stat-label">Importés</div>
                      </div>
                      <div className="ic-result-stat ic-result-stat-danger">
                        <div className="ic-result-stat-value">{importStats.failed || 0}</div>
                        <div className="ic-result-stat-label">Échecs</div>
                      </div>
                      <div className="ic-result-stat ic-result-stat-info">
                        <div className="ic-result-stat-value">{importStats.total || 0}</div>
                        <div className="ic-result-stat-label">Total</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="ic-result-error-message">
                    <i className="bi bi-exclamation-triangle"></i>
                    <div>
                      <strong>Détails de l'erreur</strong>
                      <p>{uploadResults?.error || 'Erreur inconnue'}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="ic-result-footer">
                {importStatus === 'success' ? (
                  <>
                    <button className="ic-btn-secondary" onClick={resetImport}>
                      <i className="bi bi-plus-circle"></i>
                      Nouvel import
                    </button>
                    <Link to="/apps/ListeContacts" className="ic-btn-primary">
                      <i className="bi bi-people"></i>
                      Voir les contacts
                    </Link>
                  </>
                ) : (
                  <>
                    <button className="ic-btn-secondary" onClick={() => setImportStep(2)}>
                      <i className="bi bi-arrow-left"></i>
                      Retour
                    </button>
                    <button className="ic-btn-primary" onClick={resetImport}>
                      <i className="bi bi-arrow-clockwise"></i>
                      Nouvel essai
                    </button>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .ic-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .ic-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .ic-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ic-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .ic-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .ic-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .ic-btn-primary, .ic-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .ic-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .ic-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .ic-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .ic-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .ic-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }

        /* ========== STEPPER ========== */
        .ic-stepper {
          background: white;
          border-radius: 16px;
          padding: 24px 32px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ic-stepper-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 700px;
          margin: 0 auto;
        }
        .ic-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .ic-step-circle {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          transition: all 0.3s;
          border: 2px solid #e2e8f0;
        }
        .ic-step.active .ic-step-circle {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          border-color: transparent;
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .ic-step.current .ic-step-circle {
          transform: scale(1.05);
        }
        .ic-step-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .ic-step.active .ic-step-label {
          color: #0f172a;
        }
        .ic-step-line {
          flex-grow: 1;
          height: 2px;
          background: #e2e8f0;
          margin: 0 12px;
          margin-bottom: 28px;
          transition: all 0.3s;
        }
        .ic-step-line.active {
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
        }

        /* ========== UPLOAD CARD ========== */
        .ic-upload-card,
        .ic-validation-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ic-upload-header,
        .ic-validation-header {
          padding: 20px 24px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .ic-upload-icon,
        .ic-validation-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #eff6ff;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .ic-validation-icon {
          background: #d1fae5;
          color: #10b981;
        }
        .ic-upload-title,
        .ic-validation-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .ic-upload-subtitle,
        .ic-validation-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .ic-upload-body,
        .ic-validation-body {
          padding: 32px;
        }

        /* Dropzone */
        .ic-dropzone {
          border: 2px dashed #cbd5e1;
          border-radius: 16px;
          padding: 48px 24px;
          text-align: center;
          cursor: pointer;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
          transition: all 0.3s;
        }
        .ic-dropzone:hover,
        .ic-dropzone.dragging {
          border-color: #3b82f6;
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
          transform: scale(1.005);
        }
        .ic-dropzone-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
          box-shadow: 0 12px 24px -8px rgba(59, 130, 246, 0.5);
        }
        .ic-dropzone-title {
          color: #0f172a;
          font-weight: 700;
          margin-bottom: 12px;
        }
        .ic-dropzone-text {
          color: #64748b;
          font-size: 0.875rem;
          margin-bottom: 24px;
          line-height: 1.7;
        }

        /* Format section */
        .ic-format-section {
          background: #f8fafc;
          border-radius: 12px;
          padding: 20px;
        }
        .ic-format-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .ic-format-title i {
          color: #3b82f6;
        }
        .ic-format-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ic-format-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1.2fr 2fr;
          gap: 12px;
          padding: 12px 16px;
          background: white;
          border-radius: 8px;
          align-items: center;
          border: 1px solid #e2e8f0;
        }
        .ic-format-header {
          background: transparent;
          border: none;
          padding: 8px 16px;
          font-weight: 700;
          color: #64748b;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .ic-format-cell {
          font-size: 0.82rem;
          color: #334155;
        }
        .ic-format-desc {
          color: #94a3b8;
          font-size: 0.78rem;
        }
        .ic-code {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          background: #f1f5f9;
          color: #0f172a;
          padding: 3px 8px;
          border-radius: 5px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .ic-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
        }
        .ic-badge-danger { background: #fee2e2; color: #ef4444; }
        .ic-badge-gray { background: #f1f5f9; color: #64748b; }

        /* ========== STATS ========== */
        .ic-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        .ic-stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .ic-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .ic-stat-icon-wrapper {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .ic-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .ic-stat-icon-green { background: #d1fae5; color: #10b981; }
        .ic-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .ic-stat-icon-red   { background: #fee2e2; color: #ef4444; }
        .ic-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .ic-stat-label {
          font-size: 0.68rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .ic-stat-value {
          font-size: 1.4rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
        }

        /* ========== SECTIONS ========== */
        .ic-section {
          margin-bottom: 24px;
        }
        .ic-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 14px;
        }
        .ic-section-title i {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .ic-section-title-danger {
          color: #991b1b;
        }
        .ic-section-title-danger i {
          color: #ef4444;
        }
        .ic-optional {
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.8rem;
        }

        /* Table preview */
        .ic-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        .ic-table {
          width: 100%;
          font-size: 0.82rem;
        }
        .ic-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .ic-table tbody td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
        }
        .ic-table tbody tr:hover {
          background: #f8fafc;
        }
        .ic-cell-empty {
          color: #ef4444;
          font-style: italic;
        }

        /* Errors list */
        .ic-errors-list {
          background: #fef2f2;
          border-radius: 12px;
          border: 1px solid #fecaca;
          max-height: 240px;
          overflow-y: auto;
        }
        .ic-error-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          border-bottom: 1px solid #fee2e2;
          font-size: 0.82rem;
          color: #991b1b;
        }
        .ic-error-item:last-child {
          border-bottom: none;
        }
        .ic-error-item > i {
          color: #ef4444;
          font-size: 0.95rem;
          flex-shrink: 0;
        }
        .ic-error-more {
          padding: 10px 16px;
          text-align: center;
          font-size: 0.78rem;
          color: #94a3b8;
          background: white;
        }

        /* Groups */
        .ic-groups-card {
          background: #f8fafc;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e2e8f0;
        }
        .ic-groups-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
          padding: 10px 14px;
          background: #d1fae5;
          border-radius: 8px;
          font-size: 0.82rem;
          color: #065f46;
          font-weight: 600;
        }
        .ic-groups-info > i {
          color: #10b981;
        }
        .ic-loading-inline {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          color: #64748b;
          font-size: 0.85rem;
        }

        /* Alerts */
        .ic-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
        }
        .ic-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .ic-alert-warning i {
          color: #f59e0b;
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .ic-alert-info {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border-left: 4px solid #3b82f6;
        }
        .ic-alert-info i {
          color: #3b82f6;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        /* Sending state */
        .ic-sending-state {
          text-align: center;
          padding: 40px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
          border-radius: 16px;
          margin-bottom: 24px;
          border: 1px solid #bfdbfe;
        }
        .ic-sending-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin-bottom: 20px;
          animation: ic-pulse 2s ease-in-out infinite;
          box-shadow: 0 12px 24px -8px rgba(59, 130, 246, 0.5);
        }
        @keyframes ic-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .ic-sending-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 16px;
        }
        .ic-sending-status {
          color: #3b82f6;
          font-weight: 700;
          font-size: 1.1rem;
          margin: 0 0 8px;
        }
        .ic-sending-hint {
          color: #64748b;
          font-size: 0.8rem;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .ic-progress {
          height: 8px;
          background: #f1f5f9;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 12px;
        }
        .ic-progress-large {
          height: 12px;
          max-width: 500px;
          margin: 20px auto;
        }
        .ic-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        .ic-progress-bar-animated {
          background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6);
          background-size: 200% 100%;
          animation: ic-progress-anim 2s linear infinite;
        }
        @keyframes ic-progress-anim {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        /* Actions */
        .ic-actions-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid #f1f5f9;
          margin-top: 20px;
        }

        /* ========== RESULT CARD ========== */
        .ic-result-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .ic-result-success { border-top: 4px solid #10b981; }
        .ic-result-error { border-top: 4px solid #ef4444; }
        .ic-result-header {
          padding: 32px;
          text-align: center;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .ic-result-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin-bottom: 16px;
        }
        .ic-result-icon-success {
          background: linear-gradient(135deg, #d1fae5, #a7f3d0);
          color: #10b981;
          box-shadow: 0 12px 24px -8px rgba(16, 185, 129, 0.4);
        }
        .ic-result-icon-error {
          background: linear-gradient(135deg, #fee2e2, #fecaca);
          color: #ef4444;
          box-shadow: 0 12px 24px -8px rgba(239, 68, 68, 0.4);
        }
        .ic-result-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
          font-size: 1.4rem;
        }
        .ic-result-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }
        .ic-result-body {
          padding: 32px;
        }
        .ic-result-job {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          font-size: 0.85rem;
          color: #334155;
          margin-bottom: 20px;
        }
        .ic-result-job i {
          color: #3b82f6;
        }
        .ic-result-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        .ic-result-stat {
          text-align: center;
          padding: 20px;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
        }
        .ic-result-stat-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); }
        .ic-result-stat-danger  { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); }
        .ic-result-stat-info    { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
        .ic-result-stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -0.02em;
        }
        .ic-result-stat-success .ic-result-stat-value { color: #059669; }
        .ic-result-stat-danger .ic-result-stat-value { color: #dc2626; }
        .ic-result-stat-info .ic-result-stat-value { color: #2563eb; }
        .ic-result-stat-label {
          font-size: 0.72rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 8px;
        }
        .ic-result-error-message {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 20px;
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          border-radius: 12px;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .ic-result-error-message i {
          font-size: 1.8rem;
          color: #ef4444;
          flex-shrink: 0;
        }
        .ic-result-error-message p {
          margin: 8px 0 0;
          font-size: 0.85rem;
        }
        .ic-result-footer {
          padding: 20px 32px;
          background: #fafbfc;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: center;
          gap: 12px;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .ic-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .ic-result-stats {
            grid-template-columns: 1fr;
          }
          .ic-format-row {
            grid-template-columns: 1fr 1fr;
          }
          .ic-format-header {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .ic-header {
            padding: 20px;
          }
          .ic-header-title {
            font-size: 1.2rem;
          }
          .ic-upload-body,
          .ic-validation-body {
            padding: 20px;
          }
          .ic-dropzone {
            padding: 32px 20px;
          }
          .ic-dropzone-icon {
            width: 60px;
            height: 60px;
            font-size: 28px;
          }
          .ic-actions-row {
            flex-direction: column;
          }
          .ic-actions-row > * {
            width: 100%;
          }
          .ic-result-footer {
            flex-direction: column;
          }
          .ic-result-footer > * {
            width: 100%;
          }
          .ic-step-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}